/**
 * Composant pour la gestion des documents d'un bénéficiaire individuel
 * Permet de générer et gérer tous les documents légaux (mandat, CGS, etc.)
 */

import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  generateDocumentForBeneficiary,
  saveBeneficiaryDocument,
  getBeneficiaryDocuments,
  updateDocumentStatus,
  addSignature
} from '../services/beneficiaryDocumentService';
import {
  envoyerContratMandat,
  telechargerPDFMandat
} from '../services/mandateContractService';
import { FileText, Download, Mail, CheckCircle, Clock, AlertCircle, Eye } from 'lucide-react';
import './ClientDocumentsPanel.css';

const ClientDocumentsPanel = ({ clientId, clientData }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatingDoc, setGeneratingDoc] = useState(null);

  // Types de documents disponibles
  const documentTypes = [
    {
      type: 'MANDAT',
      label: 'Contrat de Mandat',
      description: 'Contrat de prestation de services en mode mandataire',
      icon: FileText,
      canEmail: true
    },
    {
      type: 'CGS',
      label: 'Conditions Générales de Service',
      description: 'CGS applicables aux prestations',
      icon: FileText,
      canEmail: false
    },
    {
      type: 'LIVRET_ACCUEIL',
      label: 'Livret d\'Accueil',
      description: 'Informations pratiques pour le bénéficiaire',
      icon: FileText,
      canEmail: false
    },
    {
      type: 'CHARTE_DROITS',
      label: 'Charte des Droits et Libertés',
      description: 'Charte garantissant les droits du bénéficiaire',
      icon: FileText,
      canEmail: false
    },
    {
      type: 'REGLEMENT_BENEFICIAIRES',
      label: 'Règlement Intérieur Bénéficiaires',
      description: 'Règlement intérieur applicable aux bénéficiaires',
      icon: FileText,
      canEmail: false
    }
  ];

  useEffect(() => {
    if (clientId) {
      chargerDocuments();
    }
  }, [clientId]);

  /**
   * Charge tous les documents du bénéficiaire
   */
  const chargerDocuments = async () => {
    try {
      setLoading(true);
      const docs = await getBeneficiaryDocuments(db, clientId);
      setDocuments(docs);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement documents:', err);
      setError('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Génère un nouveau document
   */
  const handleGenererDocument = async (documentType) => {
    try {
      setGeneratingDoc(documentType);
      setError(null);
      setSuccess(null);

      // Vérifier si un document de ce type existe déjà
      const existingDoc = documents.find(doc => doc.documentType === documentType);
      if (existingDoc && existingDoc.status !== 'draft') {
        setError('Un document de ce type existe déjà');
        setGeneratingDoc(null);
        return;
      }

      const userId = auth.currentUser.uid;

      // Générer le document
      const documentData = await generateDocumentForBeneficiary(
        db,
        clientId,
        documentType,
        {
          companyInfo: {
            name: 'OMAD Services',
            address: '30 bis Rue des Bondoires, 18230 Saint-Doulchard'
          }
        },
        userId
      );

      // Sauvegarder le document
      const docId = await saveBeneficiaryDocument(db, clientId, documentData);

      setSuccess(`${documentTypes.find(t => t.type === documentType)?.label} généré avec succès`);
      await chargerDocuments(); // Recharger la liste
    } catch (err) {
      console.error('Erreur génération document:', err);
      setError(err.message || 'Erreur lors de la génération du document');
    } finally {
      setGeneratingDoc(null);
    }
  };

  /**
   * Envoie un document par email
   */
  const handleEnvoyerEmail = async (document) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!clientData.email) {
        throw new Error('Le bénéficiaire n\'a pas d\'adresse email');
      }

      // Utiliser la fonction d'envoi spécifique pour les mandats
      if (document.documentType === 'MANDAT') {
        await envoyerContratMandat(db, clientId, document.id, auth);
        setSuccess('Contrat de mandat envoyé par email avec succès');
      } else {
        setError('L\'envoi par email n\'est pas encore disponible pour ce type de document');
      }

      await chargerDocuments();
    } catch (err) {
      console.error('Erreur envoi email:', err);
      setError(err.message || 'Erreur lors de l\'envoi par email');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Télécharge un document en PDF
   */
  const handleTelecharger = async (document) => {
    try {
      setError(null);

      if (document.documentType === 'MANDAT') {
        await telechargerPDFMandat(db, clientId);
        setSuccess('PDF téléchargé avec succès');
      } else {
        setError('Le téléchargement PDF n\'est pas encore disponible pour ce type de document');
      }
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      setError(err.message || 'Erreur lors du téléchargement');
    }
  };

  /**
   * Affiche l'aperçu d'un document
   */
  const handleVoirApercu = (document) => {
    // Ouvrir dans une nouvelle fenêtre
    const printWindow = window.open('', '_blank');
    printWindow.document.write(document.content);
    printWindow.document.close();
  };

  /**
   * Retourne l'icône de statut
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'signed':
        return <CheckCircle size={16} className="status-icon success" />;
      case 'pending_signature':
        return <Clock size={16} className="status-icon warning" />;
      case 'draft':
        return <AlertCircle size={16} className="status-icon info" />;
      default:
        return <FileText size={16} className="status-icon" />;
    }
  };

  /**
   * Retourne le label de statut
   */
  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Brouillon',
      'pending_signature': 'En attente de signature',
      'signed': 'Signé',
      'archived': 'Archivé'
    };
    return labels[status] || status;
  };

  /**
   * Retourne le badge de statut
   */
  const getStatusBadge = (status) => {
    const badges = {
      'draft': 'status-draft',
      'pending_signature': 'status-pending',
      'signed': 'status-signed',
      'archived': 'status-archived'
    };
    return badges[status] || '';
  };

  if (loading && documents.length === 0) {
    return (
      <div className="client-docs-loading">
        <div className="spinner"></div>
        <p>Chargement des documents...</p>
      </div>
    );
  }

  return (
    <div className="client-documents-panel">
      <div className="panel-header">
        <h3>Documents du bénéficiaire</h3>
        <p className="panel-subtitle">
          {clientData?.firstName} {clientData?.lastName}
        </p>
      </div>

      {/* Messages d'alerte */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Liste des types de documents */}
      <div className="documents-grid">
        {documentTypes.map((docType) => {
          const existingDoc = documents.find(doc => doc.documentType === docType.type);
          const Icon = docType.icon;

          return (
            <div key={docType.type} className="document-card">
              <div className="document-card-header">
                <div className="document-title">
                  <Icon size={24} />
                  <span>{docType.label}</span>
                </div>
                {existingDoc && (
                  <div className={`status-badge ${getStatusBadge(existingDoc.status)}`}>
                    {getStatusIcon(existingDoc.status)}
                    <span>{getStatusLabel(existingDoc.status)}</span>
                  </div>
                )}
              </div>

              <p className="document-description">{docType.description}</p>

              {existingDoc ? (
                <div className="document-actions">
                  <button
                    className="btn-action btn-view"
                    onClick={() => handleVoirApercu(existingDoc)}
                    title="Voir l'aperçu"
                  >
                    <Eye size={16} />
                    Aperçu
                  </button>

                  <button
                    className="btn-action btn-download"
                    onClick={() => handleTelecharger(existingDoc)}
                    title="Télécharger le PDF"
                  >
                    <Download size={16} />
                    PDF
                  </button>

                  {docType.canEmail && clientData?.email && (
                    <button
                      className="btn-action btn-email"
                      onClick={() => handleEnvoyerEmail(existingDoc)}
                      disabled={loading}
                      title="Envoyer par email"
                    >
                      <Mail size={16} />
                      Email
                    </button>
                  )}
                </div>
              ) : (
                <div className="document-actions">
                  <button
                    className="btn-action btn-generate"
                    onClick={() => handleGenererDocument(docType.type)}
                    disabled={generatingDoc === docType.type}
                  >
                    {generatingDoc === docType.type ? (
                      <>
                        <div className="spinner-small"></div>
                        Génération...
                      </>
                    ) : (
                      <>
                        <FileText size={16} />
                        Générer
                      </>
                    )}
                  </button>
                </div>
              )}

              {existingDoc && existingDoc.createdAt && (
                <div className="document-meta">
                  <small>
                    Créé le {new Date(existingDoc.createdAt.seconds * 1000).toLocaleDateString('fr-FR')}
                  </small>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Section des documents existants */}
      {documents.length > 0 && (
        <div className="documents-list-section">
          <h4>Tous les documents</h4>
          <div className="documents-list">
            {documents.map((doc) => (
              <div key={doc.id} className="document-list-item">
                <div className="document-info">
                  <FileText size={20} />
                  <div>
                    <div className="document-name">{doc.documentName}</div>
                    <div className="document-date">
                      {doc.createdAt && (
                        <small>
                          Créé le {new Date(doc.createdAt.seconds * 1000).toLocaleDateString('fr-FR')}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="document-status">
                  <span className={`status-badge ${getStatusBadge(doc.status)}`}>
                    {getStatusIcon(doc.status)}
                    {getStatusLabel(doc.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDocumentsPanel;
